import * as CdsIcons from '@central-design-system/icons';

import { propsFactory } from '@components/utils';

import { CdsButton } from '../../CdsButton';

import type { NUploader } from '../interface';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { ExtractPropTypes, PropType } from 'vue';

export const makeUploadProps = propsFactory(
  {
    name: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Тип носителя откуда захватывать файл для загрузки на мобильных устройствах
     */
    capture: {
      type: [Boolean, String] as PropType<boolean | 'user' | 'environment'>,
      default: undefined
    },
    /**
     * Множественная загрузка файлов
     */
    multiple: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    /**
     * URL куда загружать файлы
     */
    action: {
      type: [String, Function] as PropType<NUploader.TAction>,
      default: undefined
    },
    /**
     * Тип http запроса для загрузки файлов
     */
    method: {
      type: String as PropType<NUploader.TUploadRequestMethod>,
      default: undefined
    },
    /**
     * Загружать директории
     */
    directory: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Список файлов по-умолчанию
     */
    defaultFileList: {
      type: Array as PropType<NUploader.IUploadFile[]>,
      default: undefined
    },
    /**
     * Показывать список загруженных файлов
     */
    showUploadList: {
      type: [Boolean, Object] as PropType<boolean | NUploader.TShowUploadList>,
      default: undefined
    },
    /**
     * Функция рендера для отображения элемента списка загружаемых файлов
     */
    itemRender: {
      type: Function as PropType<NUploader.TItemRender>,
      default: undefined
    },
    /**
     * Тип списка загружаемых файлов (поддерживается только 'text')
     */
    listType: {
      type: String as PropType<NUploader.TUploadListType>,
      default: undefined
    },
    /**
     * Параметры загрузки файла или функция возвращающая параметры загрузки файла
     */
    data: {
      type: [Object, Function] as PropType<
        Record<string, unknown> | ((file: NUploader.IRcFile | string | Blob) => Record<string, unknown>)
      >,
      default: undefined
    },
    /**
     * Заголовки запроса
     */
    headers: {
      type: Object as PropType<NUploader.TUploadRequestHeaders>,
      default: undefined
    },
    /**
     * Типы разрешенных файлов
     */
    accept: {
      type: [String, Array] as PropType<string | string[]>,
      default: undefined
    },
    /**
     * Собственный запрос на загрузку файла. Переопределяет xhr по-умолчанию
     */
    customRequest: {
      type: Function as PropType<(options: NUploader.IUploadRequestOption) => void>,
      default: undefined
    },
    /**
     * Запрос с передачей cookie
     */
    withCredentials: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Открывать ли системное окно при клике на загрузчик файлов
     */
    openFileDialogOnClick: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Хук жизненного цикла перед загрузкой файла
     */
    beforeUpload: {
      type: Function as PropType<
        (
          file: NUploader.IUploadFile,
          fileList: NUploader.IUploadFile[]
        ) => NUploader.TBeforeUploadFileType | Promise<void | NUploader.TBeforeUploadFileType>
      >,
      default: undefined
    },
    /**
     * Максимальное количество загружаемых файлов
     */
    maxCount: {
      type: Number as PropType<number>,
      default: undefined
    },
    /**
     * Настройка отображения миниатюр загружаемых файлов (пока не поддерживается)
     */
    isImageUrl: {
      type: Function as PropType<(file: NUploader.IUploadFile) => boolean>,
      default: undefined
    },
    /**
     * Показывать ли тултип с ошибкой возле каждого файла
     */
    showTooltipWithError: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /*
     * Свойства для компонента CdsButton
     */
    buttonProps: {
      type: Object as PropType<typeof CdsButton>,
      default: null
    },
    /**
     * Иконка удаления файла
     */
    removeIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'delete',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    /**
     * Иконка скачивания файла
     */
    downloadIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'download',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    /**
     * Иконка загрузки файла
     */
    uploadIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'upload',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    }
  },
  'upload'
);

export interface IUploadProps<T = any> extends Partial<ExtractPropTypes<ReturnType<typeof makeUploadProps>>> {
  onReject?: (file: NUploader.IUploadFile<T>) => void;
  onChange?: (info: NUploader.IUploadChangeParam<NUploader.IUploadFile<T>>) => void;
  onDrop?: (event: DragEvent) => void;
  onPreview?: (file: NUploader.IUploadFile<T>) => void;
  onDownload?: (file: NUploader.IUploadFile<T>) => void;
  onRemove?: (file: NUploader.IUploadFile<T>) => void | boolean | Promise<void | boolean>;
  'onUpdate:modelValue'?: (fileList: NUploader.IUploadChangeParam<NUploader.IUploadFile<T>>['fileList']) => void;
}

export const makeUploadListProps = propsFactory(
  {
    items: {
      type: Array as PropType<NUploader.IUploadFile[]>,
      default: undefined
    },
    listType: {
      type: String as PropType<NUploader.TUploadListType>,
      default: undefined
    },
    itemRender: {
      type: Function as PropType<NUploader.TItemRender>,
      default: undefined
    },
    showRemoveIcon: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    showDownloadIcon: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    showPreviewIcon: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    showTooltipWithError: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    removeIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'delete',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    downloadIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'download',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    uploadIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'upload',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    isImageUrl: {
      type: Function as PropType<(file: NUploader.IUploadFile) => boolean>,
      default: undefined
    },
    progress: {
      type: Object as PropType<NUploader.TUploadListProgressProps>,
      default: undefined
    },
    onDownload: {
      type: Function as PropType<(file: NUploader.IUploadFile) => void>,
      default: undefined
    },
    onPreview: {
      type: Function as PropType<(file: NUploader.IUploadFile) => void>,
      default: undefined
    },
    onRemove: {
      type: Function as PropType<(file: NUploader.IUploadFile) => void>,
      default: undefined
    }
  },
  'upload-list'
);
